import { useState, useEffect } from 'react';
import {
  Users, BarChart3, Link2, Search, ChevronLeft, ChevronRight, ChevronDown,
  Shield, ShieldOff, Trash2, Crown, AlertTriangle,
  RefreshCw, CheckCircle, XCircle, Download, Eye, EyeOff,
  Settings, UserPlus, UserMinus, Plus, X,
} from 'lucide-react';
import {
  api,
  AdminPlatformStats,
  AdminCustomer,
  AdminCustomerDetail,
  AdminTeamMember,
  Pagination,
  CRMConfig,
  CFTag,
  CFWorkspaceOption,
  CRMDelivery,
  WebhookLog,
  StripeSettings,
  LimitsConfig,
} from '../lib/api';

type AdminView = 'overview' | 'users' | 'user-detail' | 'integrations' | 'settings';

export default function AdminPanel() {
  const [view, setView] = useState<AdminView>('overview');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const openUserDetail = (id: string) => {
    setSelectedUserId(id);
    setView('user-detail');
  };

  const goBack = () => {
    setView('users');
    setSelectedUserId(null);
  };

  return (
    <div className="space-y-6">
      {/* Admin header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3">
          <Crown className="w-6 h-6" />
          <h2 className="text-xl font-bold">Admin Panel</h2>
        </div>
        <p className="text-purple-200 text-sm mt-1">Platform management and user administration</p>
      </div>

      {/* Sub-navigation */}
      {view !== 'user-detail' && (
        <div className="flex gap-2 border-b pb-3">
          {([
            { id: 'overview', label: 'Platform Stats', icon: BarChart3 },
            { id: 'users', label: 'User Management', icon: Users },
            { id: 'integrations', label: 'CRM Connections', icon: Link2 },
            { id: 'settings', label: 'Settings', icon: Settings },
          ] as const).map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  view === tab.id
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {view === 'overview' && <PlatformOverview onViewUsers={() => setView('users')} />}
      {view === 'users' && <UserManagement onSelectUser={openUserDetail} />}
      {view === 'user-detail' && selectedUserId && (
        <UserDetail userId={selectedUserId} onBack={goBack} />
      )}
      {view === 'integrations' && <CRMConfiguration />}
      {view === 'settings' && <PlatformSettings />}
    </div>
  );
}

// ============ Platform Overview ============

function PlatformOverview({ onViewUsers }: { onViewUsers: () => void }) {
  const [stats, setStats] = useState<AdminPlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAdminStats()
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Top-level metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Customers" value={stats.customers.total} />
        <StatCard label="Active Customers" value={stats.customers.active} />
        <StatCard label="Searches Today" value={stats.searches.today} />
        <StatCard label="Searches (30d)" value={stats.searches.month} />
      </div>

      {/* Plan breakdown + content stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Billing / Plan breakdown */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customers by Plan</h3>
          <div className="space-y-3">
            {stats.customers.by_plan.map(bp => (
              <div key={bp.plan} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                    bp.plan === 'platinum' ? 'bg-amber-100 text-amber-800'
                    : bp.plan === 'advanced' ? 'bg-purple-100 text-purple-800'
                    : bp.plan === 'pro' ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                  }`}>
                    {bp.plan}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        bp.plan === 'platinum' ? 'bg-amber-500'
                        : bp.plan === 'advanced' ? 'bg-purple-500'
                        : bp.plan === 'pro' ? 'bg-blue-500'
                        : 'bg-gray-400'
                      }`}
                      style={{ width: `${stats.customers.total ? (bp.count / stats.customers.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 w-8 text-right">{bp.count}</span>
                </div>
              </div>
            ))}
            {stats.customers.by_plan.length === 0 && (
              <p className="text-sm text-gray-500">No customers yet</p>
            )}
          </div>
          <button
            onClick={onViewUsers}
            className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Manage Users →
          </button>
        </div>

        {/* Content stats */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Content</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Courses</span>
              <span className="text-lg font-semibold text-gray-900">{stats.content.courses}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Lessons</span>
              <span className="text-lg font-semibold text-gray-900">{stats.content.lessons}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total API Keys</span>
              <span className="text-lg font-semibold text-gray-900">{stats.content.api_keys}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">ClickFunnels Connections</span>
              <span className="text-lg font-semibold text-gray-900">{stats.integrations.clickfunnels}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ User Management ============

function UserManagement({ onSelectUser }: { onSelectUser: (id: string) => void }) {
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCustomers = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getAdminCustomers({ page, limit: 20, search, plan: planFilter });
      setCustomers(res.customers);
      setPagination(res.pagination);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, [search, planFilter]);

  const handleToggle = async (id: string) => {
    try {
      const result = await api.toggleCustomerActive(id);
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, is_active: result.is_active } : c));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  const handleDelete = async (id: string, email: string) => {
    if (!confirm(`Delete customer ${email}? This cannot be undone.`)) return;
    try {
      await api.deleteCustomer(id);
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      {error && <ErrorMessage message={error} />}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by email or company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <select
          value={planFilter}
          onChange={e => setPlanFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All Plans</option>
          <option value="pro">Pro</option>
          <option value="advanced">Advanced</option>
          <option value="platinum">Platinum</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8"><LoadingSpinner /></div>
        ) : customers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No customers found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Customer</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Plan</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Courses</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Lessons</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Searches (30d)</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {customers.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onSelectUser(c.id)}
                        className="text-left hover:text-indigo-600"
                      >
                        <div className="font-medium text-gray-900 flex items-center gap-1.5">
                          {c.company_name}
                          {c.is_admin && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                        </div>
                        <div className="text-gray-500 text-xs">{c.email}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        c.plan === 'platinum' ? 'bg-amber-100 text-amber-800'
                        : c.plan === 'advanced' ? 'bg-purple-100 text-purple-800'
                        : c.plan === 'pro' ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                      }`}>
                        {c.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{c.courses_count}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{c.lessons_count}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{c.searches_30d}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {c.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggle(c.id)}
                          title={c.is_active ? 'Disable' : 'Enable'}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        >
                          {c.is_active ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                        </button>
                        {!c.is_admin && (
                          <button
                            onClick={() => handleDelete(c.id, c.email)}
                            title="Delete"
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-600">
              {pagination.total} customer{pagination.total !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchCustomers(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => fetchCustomers(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ User Detail ============

function UserDetail({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [customer, setCustomer] = useState<AdminCustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [planSaving, setPlanSaving] = useState(false);

  // Add member form
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('viewer');
  const [addingMember, setAddingMember] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  useEffect(() => {
    api.getAdminCustomerDetail(userId)
      .then(setCustomer)
      .catch(e => setErrMsg(e.message))
      .finally(() => setLoading(false));
  }, [userId]);

  const handlePlanChange = async (plan: string) => {
    if (!customer) return;
    setPlanSaving(true);
    try {
      await api.updateCustomerPlan(userId, plan);
      setCustomer({ ...customer, plan });
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleToggle = async () => {
    if (!customer) return;
    try {
      const result = await api.toggleCustomerActive(userId);
      setCustomer({ ...customer, is_active: result.is_active });
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Failed');
    }
  };

  const handleAddMember = async () => {
    if (!customer || !newMemberEmail.trim()) return;
    setAddingMember(true);
    setErrMsg('');
    try {
      const member = await api.adminAddMember(userId, newMemberEmail.trim(), newMemberRole);
      setCustomer({ ...customer, members: [...customer.members, member] });
      setNewMemberEmail('');
      setNewMemberRole('viewer');
      setShowAddMember(false);
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRoleChange = async (memberId: string, role: string) => {
    if (!customer) return;
    try {
      await api.adminUpdateMemberRole(memberId, role);
      setCustomer({
        ...customer,
        members: customer.members.map(m =>
          m.id === memberId ? { ...m, role: role as AdminTeamMember['role'] } : m
        ),
      });
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!customer) return;
    try {
      await api.adminRemoveMember(memberId);
      setCustomer({
        ...customer,
        members: customer.members.filter(m => m.id !== memberId),
      });
      setConfirmRemove(null);
    } catch (e: unknown) {
      setErrMsg(e instanceof Error ? e.message : 'Failed to remove member');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (errMsg && !customer) return <ErrorMessage message={errMsg} />;
  if (!customer) return null;

  const roleColors: Record<string, string> = {
    owner: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    editor: 'bg-green-100 text-green-800',
    viewer: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ChevronLeft className="w-4 h-4" /> Back to Users
      </button>

      {errMsg && <ErrorMessage message={errMsg} />}

      {/* Customer header */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              {customer.company_name}
              {customer.is_admin && <Crown className="w-4 h-4 text-amber-500" />}
            </h3>
            <p className="text-sm text-gray-500">{customer.email}</p>
            <p className="text-xs text-gray-400 mt-1">
              Joined {new Date(customer.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
              customer.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {customer.is_active ? 'Active' : 'Disabled'}
            </span>
            <button
              onClick={handleToggle}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                customer.is_active
                  ? 'border-red-300 text-red-700 hover:bg-red-50'
                  : 'border-green-300 text-green-700 hover:bg-green-50'
              }`}
            >
              {customer.is_active ? 'Disable Account' : 'Enable Account'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan management */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Plan</h4>
          <div className="flex gap-2">
            {['pro', 'advanced', 'platinum'].map(plan => (
              <button
                key={plan}
                onClick={() => handlePlanChange(plan)}
                disabled={planSaving}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors capitalize ${
                  customer.plan === plan
                    ? plan === 'platinum' ? 'bg-amber-50 border-amber-300 text-amber-700'
                      : plan === 'advanced' ? 'bg-purple-50 border-purple-300 text-purple-700'
                      : plan === 'pro' ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-gray-100 border-gray-300 text-gray-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {plan}
              </button>
            ))}
          </div>
        </div>

        {/* Usage stats */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Usage</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-2xl font-bold text-gray-900">{customer.stats.courses}</div>
              <div className="text-xs text-gray-500">Courses</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{customer.stats.api_keys}</div>
              <div className="text-xs text-gray-500">API Keys</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{customer.stats.domains}</div>
              <div className="text-xs text-gray-500">Domains</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{customer.stats.searches_30d}</div>
              <div className="text-xs text-gray-500">Searches (30d)</div>
            </div>
          </div>
          {customer.stats.integrations > 0 && (
            <div className="mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">
                {customer.stats.integrations} ClickFunnels connection{customer.stats.integrations > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Team Members ({customer.members?.length || 0})
            </h4>
            <p className="text-xs text-gray-500 mt-1">Users with access to this organization</p>
          </div>
          <button
            onClick={() => setShowAddMember(!showAddMember)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            <UserPlus className="w-4 h-4" />
            Add Member
          </button>
        </div>

        {/* Add member form */}
        {showAddMember && (
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="user@example.com"
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <select
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
              <button
                onClick={handleAddMember}
                disabled={addingMember || !newMemberEmail.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {addingMember ? 'Adding...' : 'Add'}
              </button>
              <button
                onClick={() => { setShowAddMember(false); setNewMemberEmail(''); }}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              The user must already have an account. They will be added directly without an invitation.
            </p>
          </div>
        )}

        {/* Members table */}
        <div className="overflow-x-auto">
          {(!customer.members || customer.members.length === 0) ? (
            <div className="p-6 text-center text-sm text-gray-500">
              No team members found for this organization.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {customer.members.map(member => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{member.name}</div>
                      <div className="text-xs text-gray-500">{member.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(member.id, e.target.value)}
                        className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer ${roleColors[member.role] || 'bg-gray-100 text-gray-700'}`}
                      >
                        <option value="owner">Owner</option>
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {member.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {member.accepted_at
                        ? new Date(member.accepted_at).toLocaleDateString()
                        : 'Pending'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {confirmRemove === member.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-red-600">Remove?</span>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setConfirmRemove(null)}
                            className="text-xs px-2 py-1 border rounded hover:bg-gray-100"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemove(member.id)}
                          className="text-gray-400 hover:text-red-600"
                          title="Remove member"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ CRM Configuration ============

type CRMSubView = 'config' | 'deliveries';

function CRMConfiguration() {
  const [subView, setSubView] = useState<CRMSubView>('config');
  const [config, setConfig] = useState<CRMConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ClickFunnels-specific state
  const [cfApiToken, setCfApiToken] = useState('');
  const [cfWorkspace, setCfWorkspace] = useState('');
  const [cfWorkspaceId, setCfWorkspaceId] = useState('');
  const [cfWorkspaces, setCfWorkspaces] = useState<CFWorkspaceOption[]>([]);
  const [cfTags, setCfTags] = useState<CFTag[]>([]);
  const [cfTagPro, setCfTagPro] = useState('');
  const [cfTagProId, setCfTagProId] = useState('');
  const [cfTagAdvanced, setCfTagAdvanced] = useState('');
  const [cfTagAdvancedId, setCfTagAdvancedId] = useState('');
  const [cfTagPlatinum, setCfTagPlatinum] = useState('');
  const [cfTagPlatinumId, setCfTagPlatinumId] = useState('');
  const [fetchingWorkspaces, setFetchingWorkspaces] = useState(false);
  const [fetchingTags, setFetchingTags] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);

  // GoHighLevel-specific state
  const [ghlWebhookUrl, setGhlWebhookUrl] = useState('');
  const [ghlLocationId, setGhlLocationId] = useState('');
  const [ghlTagPro, setGhlTagPro] = useState('');
  const [ghlTagAdvanced, setGhlTagAdvanced] = useState('');
  const [ghlTagPlatinum, setGhlTagPlatinum] = useState('');

  // Platform selection
  const [platform, setPlatform] = useState<'none' | 'clickfunnels' | 'gohighlevel'>('none');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);

  // Delivery log state
  const [deliveries, setDeliveries] = useState<CRMDelivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    try {
      setLoading(true);
      const data = await api.getCRMConfig();
      setConfig(data);
      setPlatform(data.platform);
      // ClickFunnels
      setCfApiToken(''); // Don't show masked token in input
      setCfWorkspace(data.cf_workspace);
      setCfWorkspaceId(data.cf_workspace_id);
      setCfTagPro(data.cf_tag_pro);
      setCfTagProId(data.cf_tag_pro_id);
      setCfTagAdvanced(data.cf_tag_advanced);
      setCfTagAdvancedId(data.cf_tag_advanced_id);
      setCfTagPlatinum(data.cf_tag_platinum);
      setCfTagPlatinumId(data.cf_tag_platinum_id);
      // GoHighLevel
      setGhlWebhookUrl(data.ghl_webhook_url);
      setGhlLocationId(data.ghl_location_id);
      setGhlTagPro(data.ghl_tag_pro);
      setGhlTagAdvanced(data.ghl_tag_advanced);
      setGhlTagPlatinum(data.ghl_tag_platinum);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load CRM config');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const updates: Partial<CRMConfig> = { platform };

      if (platform === 'clickfunnels') {
        if (cfApiToken) updates.cf_api_token = cfApiToken; // Only update if changed
        updates.cf_workspace = cfWorkspace;
        updates.cf_workspace_id = cfWorkspaceId;
        updates.cf_tag_pro = cfTagPro;
        updates.cf_tag_pro_id = cfTagProId;
        updates.cf_tag_advanced = cfTagAdvanced;
        updates.cf_tag_advanced_id = cfTagAdvancedId;
        updates.cf_tag_platinum = cfTagPlatinum;
        updates.cf_tag_platinum_id = cfTagPlatinumId;
      } else if (platform === 'gohighlevel') {
        updates.ghl_webhook_url = ghlWebhookUrl;
        updates.ghl_location_id = ghlLocationId;
        updates.ghl_tag_pro = ghlTagPro;
        updates.ghl_tag_advanced = ghlTagAdvanced;
        updates.ghl_tag_platinum = ghlTagPlatinum;
      }

      const result = await api.saveCRMConfig(updates);
      setConfig(result);
      setSuccessMsg('CRM configuration saved successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleFetchWorkspaces() {
    const token = cfApiToken || prompt('Enter your ClickFunnels API token:');
    if (!token || !cfWorkspace) {
      setError('API token and workspace subdomain are required');
      return;
    }
    setFetchingWorkspaces(true);
    setError('');
    try {
      const result = await api.fetchCFWorkspaces(token, cfWorkspace);
      setCfWorkspaces(result.workspaces);
      if (result.workspaces.length === 0) {
        setError('No workspaces found. Check your API token and workspace subdomain.');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch workspaces');
    } finally {
      setFetchingWorkspaces(false);
    }
  }

  async function handleFetchTags() {
    const token = cfApiToken || prompt('Enter your ClickFunnels API token:');
    if (!token || !cfWorkspace || !cfWorkspaceId) {
      setError('API token, workspace subdomain, and workspace ID are required');
      return;
    }
    setFetchingTags(true);
    setError('');
    try {
      const result = await api.fetchCFTags(token, cfWorkspace, cfWorkspaceId);
      setCfTags(result.tags);

      // Auto-match saved tag names to IDs if we have names but no IDs
      for (const [getName, getIdName, setId] of [
        [cfTagPro, cfTagProId, setCfTagProId],
        [cfTagAdvanced, cfTagAdvancedId, setCfTagAdvancedId],
        [cfTagPlatinum, cfTagPlatinumId, setCfTagPlatinumId],
      ] as [string, string, (v: string) => void][]) {
        if (getName && !getIdName) {
          const match = result.tags.find(t => t.name.toLowerCase() === getName.toLowerCase());
          if (match) setId(String(match.id));
        }
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch tags');
    } finally {
      setFetchingTags(false);
    }
  }

  async function handleTestConnection() {
    setTesting(true);
    setError('');
    setSuccessMsg('');
    try {
      const result = await api.testCRMConnection();
      if (result.success) {
        setSuccessMsg(result.message);
        setTimeout(() => setSuccessMsg(''), 5000);
      } else {
        setError(result.message);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  }

  async function handleSyncAll() {
    if (!confirm('This will sync all active users to the CRM. Continue?')) return;
    setSyncing(true);
    setError('');
    setSuccessMsg('');
    try {
      const result = await api.syncAllUsersToCRM();
      setSuccessMsg(`Sync complete: ${result.synced}/${result.total} synced, ${result.failed} failed`);
      if (result.errors.length > 0) {
        setError(`Errors: ${result.errors.slice(0, 5).join('; ')}${result.errors.length > 5 ? '...' : ''}`);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function loadDeliveries() {
    setDeliveriesLoading(true);
    try {
      const result = await api.getCRMDeliveries(50);
      setDeliveries(result.deliveries);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load deliveries');
    } finally {
      setDeliveriesLoading(false);
    }
  }

  function handleTagSelect(tagIdStr: string, setName: (v: string) => void, setId: (v: string) => void) {
    const selectedTag = cfTags.find(t => String(t.id) === tagIdStr);
    setId(tagIdStr);
    setName(selectedTag?.name || '');
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Sub-tabs: Config vs Delivery Log */}
      <div className="flex gap-2">
        <button
          onClick={() => setSubView('config')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            subView === 'config' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Configuration
        </button>
        <button
          onClick={() => { setSubView('deliveries'); loadDeliveries(); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            subView === 'deliveries' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Delivery Log
        </button>
      </div>

      {error && <ErrorMessage message={error} />}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {successMsg}
        </div>
      )}

      {subView === 'config' && (
        <div className="space-y-6">
          {/* Platform Selector */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">CRM Platform</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select which CRM to sync your users to. Users will be tagged based on their subscription plan.
            </p>
            <div className="flex gap-3">
              {([
                { value: 'none', label: 'None', desc: 'CRM sync disabled' },
                { value: 'clickfunnels', label: 'ClickFunnels', desc: 'API-based sync' },
                { value: 'gohighlevel', label: 'GoHighLevel', desc: 'Webhook-based sync' },
              ] as const).map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPlatform(opt.value)}
                  className={`flex-1 p-4 rounded-lg border-2 text-left transition-colors ${
                    platform === opt.value
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{opt.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ClickFunnels Configuration */}
          {platform === 'clickfunnels' && (
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <img src="https://www.google.com/s2/favicons?domain=myclickfunnels.com&sz=32" alt="" className="w-5 h-5" />
                ClickFunnels Configuration
              </h3>

              {/* API Token */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Token</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showApiToken ? 'text' : 'password'}
                      value={cfApiToken}
                      onChange={e => setCfApiToken(e.target.value)}
                      placeholder={config?.cf_api_token ? 'Token saved (enter new to update)' : 'Enter ClickFunnels API token'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiToken(!showApiToken)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showApiToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {config?.cf_api_token && (
                  <p className="text-xs text-gray-500 mt-1">Current: {config.cf_api_token}</p>
                )}
              </div>

              {/* Workspace Subdomain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Workspace Subdomain</label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center flex-1">
                    <input
                      type="text"
                      value={cfWorkspace}
                      onChange={e => setCfWorkspace(e.target.value)}
                      placeholder="mycompany"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg text-sm"
                    />
                    <span className="px-3 py-2 bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg text-gray-500 text-sm">
                      .myclickfunnels.com
                    </span>
                  </div>
                  <button
                    onClick={handleFetchWorkspaces}
                    disabled={fetchingWorkspaces || !cfWorkspace}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {fetchingWorkspaces ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Fetch Workspaces
                  </button>
                </div>
              </div>

              {/* Workspace Selector */}
              {cfWorkspaces.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Workspace</label>
                  <select
                    value={cfWorkspaceId}
                    onChange={e => {
                      setCfWorkspaceId(e.target.value);
                      const ws = cfWorkspaces.find(w => String(w.id) === e.target.value);
                      if (ws) setCfWorkspace(ws.subdomain);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Select a workspace...</option>
                    {cfWorkspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name} ({ws.subdomain}.myclickfunnels.com)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Current Workspace ID */}
              {cfWorkspaceId && cfWorkspaces.length === 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Workspace ID</label>
                  <input
                    type="text"
                    value={cfWorkspaceId}
                    onChange={e => setCfWorkspaceId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  />
                </div>
              )}

              {/* Fetch Tags */}
              {cfWorkspaceId && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Tag Mapping</label>
                    <button
                      onClick={handleFetchTags}
                      disabled={fetchingTags}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {fetchingTags ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Fetch Tags from ClickFunnels
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Assign a ClickFunnels tag to each plan. When users are synced, they get the tag matching their plan.
                  </p>

                  {cfTags.length > 0 ? (
                    <div className="space-y-3">
                      {([
                        { plan: 'Pro', tagName: cfTagPro, tagId: cfTagProId, setName: setCfTagPro, setId: setCfTagProId },
                        { plan: 'Advanced', tagName: cfTagAdvanced, tagId: cfTagAdvancedId, setName: setCfTagAdvanced, setId: setCfTagAdvancedId },
                        { plan: 'Platinum', tagName: cfTagPlatinum, tagId: cfTagPlatinumId, setName: setCfTagPlatinum, setId: setCfTagPlatinumId },
                      ]).map(({ plan, tagId, setName, setId }) => (
                        <div key={plan} className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-24 justify-center ${
                            plan === 'Platinum' ? 'bg-purple-100 text-purple-800'
                            : plan === 'Advanced' ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}>
                            {plan}
                          </span>
                          <select
                            value={tagId}
                            onChange={e => handleTagSelect(e.target.value, setName, setId)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">No tag assigned</option>
                            {cfTags.map(tag => (
                              <option key={tag.id} value={tag.id}>
                                {tag.name} (ID: {tag.id})
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Show manual tag name/ID inputs when tags haven't been fetched */}
                      {([
                        { plan: 'Pro', name: cfTagPro, id: cfTagProId, setName: setCfTagPro, setId: setCfTagProId },
                        { plan: 'Advanced', name: cfTagAdvanced, id: cfTagAdvancedId, setName: setCfTagAdvanced, setId: setCfTagAdvancedId },
                        { plan: 'Platinum', name: cfTagPlatinum, id: cfTagPlatinumId, setName: setCfTagPlatinum, setId: setCfTagPlatinumId },
                      ]).map(({ plan, name, id, setName, setId }) => (
                        <div key={plan} className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-24 justify-center ${
                            plan === 'Platinum' ? 'bg-purple-100 text-purple-800'
                            : plan === 'Advanced' ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-blue-100 text-blue-800'
                          }`}>
                            {plan}
                          </span>
                          <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Tag name"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <input
                            type="text"
                            value={id}
                            onChange={e => setId(e.target.value)}
                            placeholder="Tag ID"
                            className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      ))}
                      <p className="text-xs text-gray-400">
                        Click "Fetch Tags" to load tags from ClickFunnels, or enter them manually.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* GoHighLevel Configuration */}
          {platform === 'gohighlevel' && (
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
              <h3 className="text-lg font-semibold text-gray-900">GoHighLevel Configuration</h3>
              <p className="text-sm text-gray-500">
                Create an inbound webhook workflow in GoHighLevel and paste the URL below.
              </p>

              {/* Webhook URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
                <input
                  type="url"
                  value={ghlWebhookUrl}
                  onChange={e => setGhlWebhookUrl(e.target.value)}
                  placeholder="https://services.leadconnectorhq.com/hooks/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Location ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location ID (optional)</label>
                <input
                  type="text"
                  value={ghlLocationId}
                  onChange={e => setGhlLocationId(e.target.value)}
                  placeholder="abc123xyz"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Tag Names */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Tag Mapping</label>
                <p className="text-xs text-gray-500 mb-3">
                  Enter the tag names to send with each plan level. These will be included in the webhook payload.
                </p>
                <div className="space-y-3">
                  {([
                    { plan: 'Pro', value: ghlTagPro, set: setGhlTagPro },
                    { plan: 'Advanced', value: ghlTagAdvanced, set: setGhlTagAdvanced },
                    { plan: 'Platinum', value: ghlTagPlatinum, set: setGhlTagPlatinum },
                  ]).map(({ plan, value, set }) => (
                    <div key={plan} className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-24 justify-center ${
                        plan === 'Platinum' ? 'bg-purple-100 text-purple-800'
                        : plan === 'Advanced' ? 'bg-indigo-100 text-indigo-800'
                        : 'bg-blue-100 text-blue-800'
                      }`}>
                        {plan}
                      </span>
                      <input
                        type="text"
                        value={value}
                        onChange={e => set(e.target.value)}
                        placeholder={`Tag for ${plan} plan`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {platform !== 'none' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Save Configuration
                </button>
                <button
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Test Connection
                </button>
                <button
                  onClick={handleSyncAll}
                  disabled={syncing}
                  className="px-4 py-2.5 border border-indigo-300 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  Sync All Users
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                "Sync All Users" will send all active users to the CRM with their current plan tags.
              </p>
            </div>
          )}

          {/* Save for platform=none */}
          {platform === 'none' && config?.platform !== 'none' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save (Disable CRM)'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delivery Log */}
      {subView === 'deliveries' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Recent CRM Deliveries</h3>
            <button
              onClick={loadDeliveries}
              disabled={deliveriesLoading}
              className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500"
            >
              <RefreshCw className={`w-4 h-4 ${deliveriesLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {deliveriesLoading ? (
            <div className="p-8"><LoadingSpinner /></div>
          ) : deliveries.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">No delivery logs yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Time</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Platform</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Event</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                    <th className="text-center px-4 py-2 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deliveries.map(d => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">
                        {new Date(d.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-gray-700 capitalize">{d.platform}</td>
                      <td className="px-4 py-2 text-gray-700">{d.event_type}</td>
                      <td className="px-4 py-2 text-gray-700">{d.user_email}</td>
                      <td className="px-4 py-2 text-center">
                        {d.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 inline" />
                        ) : d.status === 'failed' ? (
                          <XCircle className="w-4 h-4 text-red-500 inline" />
                        ) : (
                          <RefreshCw className="w-4 h-4 text-yellow-500 inline" />
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500 max-w-xs truncate">
                        {d.error_message || d.response_body || (d.status_code ? `HTTP ${d.status_code}` : '-')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Accordion ============

function Accordion({ title, subtitle, children, defaultOpen = false }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t">{children}</div>}
    </div>
  );
}

// ============ Platform Settings ============

function PlatformSettings() {
  const [loading, setLoading] = useState(true);
  const [savingStripe, setSavingStripe] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  const [savedStripe, setSavedStripe] = useState(false);
  const [savedTiers, setSavedTiers] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Stripe fields
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [proMonthlyPriceId, setProMonthlyPriceId] = useState('');
  const [proYearlyPriceId, setProYearlyPriceId] = useState('');
  const [advMonthlyPriceId, setAdvMonthlyPriceId] = useState('');
  const [advYearlyPriceId, setAdvYearlyPriceId] = useState('');
  const [platMonthlyPriceId, setPlatMonthlyPriceId] = useState('');
  const [platYearlyPriceId, setPlatYearlyPriceId] = useState('');
  const [currentSecretKey, setCurrentSecretKey] = useState('');
  const [currentWebhookSecret, setCurrentWebhookSecret] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // Tier config fields
  const [proMonthlyPrice, setProMonthlyPrice] = useState('29');
  const [proYearlyPrice, setProYearlyPrice] = useState('290');
  const [advMonthlyPrice, setAdvMonthlyPrice] = useState('79');
  const [advYearlyPrice, setAdvYearlyPrice] = useState('790');
  const [platMonthlyPrice, setPlatMonthlyPrice] = useState('199');
  const [platYearlyPrice, setPlatYearlyPrice] = useState('1990');
  const [proFeatures, setProFeatures] = useState<string[]>([]);
  const [advFeatures, setAdvFeatures] = useState<string[]>([]);
  const [platFeatures, setPlatFeatures] = useState<string[]>([]);

  // Webhook logs
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.getStripeSettings(), api.getAdminTierConfig()])
      .then(([stripe, tiers]) => {
        setCurrentSecretKey(stripe.secret_key);
        setCurrentWebhookSecret(stripe.webhook_secret);
        setProMonthlyPriceId(stripe.pro_monthly_price_id);
        setProYearlyPriceId(stripe.pro_yearly_price_id);
        setAdvMonthlyPriceId(stripe.advanced_monthly_price_id);
        setAdvYearlyPriceId(stripe.advanced_yearly_price_id);
        setPlatMonthlyPriceId(stripe.platinum_monthly_price_id);
        setPlatYearlyPriceId(stripe.platinum_yearly_price_id);

        setProMonthlyPrice(tiers.pro_monthly_price);
        setProYearlyPrice(tiers.pro_yearly_price);
        setAdvMonthlyPrice(tiers.advanced_monthly_price);
        setAdvYearlyPrice(tiers.advanced_yearly_price);
        setPlatMonthlyPrice(tiers.platinum_monthly_price);
        setPlatYearlyPrice(tiers.platinum_yearly_price);
        setProFeatures(tiers.pro_features ? tiers.pro_features.split('|') : []);
        setAdvFeatures(tiers.advanced_features ? tiers.advanced_features.split('|') : []);
        setPlatFeatures(tiers.platinum_features ? tiers.platinum_features.split('|') : []);
      })
      .catch((e) => setErrMsg(e.message))
      .finally(() => setLoading(false));
  }, []);

  const loadWebhookLogs = async () => {
    setLogsLoading(true);
    try {
      const result = await api.getWebhookLogs(50);
      setWebhookLogs(result.logs);
    } catch (e: any) {
      setErrMsg(e.message);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSaveStripe = async () => {
    setSavingStripe(true);
    setErrMsg('');
    setSavedStripe(false);
    try {
      const updates: Partial<StripeSettings> = {};
      if (secretKey) updates.secret_key = secretKey;
      if (webhookSecret) updates.webhook_secret = webhookSecret;
      updates.pro_monthly_price_id = proMonthlyPriceId;
      updates.pro_yearly_price_id = proYearlyPriceId;
      updates.advanced_monthly_price_id = advMonthlyPriceId;
      updates.advanced_yearly_price_id = advYearlyPriceId;
      updates.platinum_monthly_price_id = platMonthlyPriceId;
      updates.platinum_yearly_price_id = platYearlyPriceId;

      const result = await api.saveStripeSettings(updates);
      setCurrentSecretKey(result.secret_key);
      setCurrentWebhookSecret(result.webhook_secret);
      setProMonthlyPriceId(result.pro_monthly_price_id);
      setProYearlyPriceId(result.pro_yearly_price_id);
      setAdvMonthlyPriceId(result.advanced_monthly_price_id);
      setAdvYearlyPriceId(result.advanced_yearly_price_id);
      setPlatMonthlyPriceId(result.platinum_monthly_price_id);
      setPlatYearlyPriceId(result.platinum_yearly_price_id);
      setSecretKey('');
      setWebhookSecret('');
      setSavedStripe(true);
      setTimeout(() => setSavedStripe(false), 3000);
    } catch (e: any) {
      setErrMsg(e.message);
    } finally {
      setSavingStripe(false);
    }
  };

  const handleSaveTiers = async () => {
    setSavingTiers(true);
    setErrMsg('');
    setSavedTiers(false);
    try {
      const result = await api.saveAdminTierConfig({
        pro_monthly_price: proMonthlyPrice,
        pro_yearly_price: proYearlyPrice,
        advanced_monthly_price: advMonthlyPrice,
        advanced_yearly_price: advYearlyPrice,
        platinum_monthly_price: platMonthlyPrice,
        platinum_yearly_price: platYearlyPrice,
        pro_features: proFeatures.filter(f => f.trim()).join('|'),
        advanced_features: advFeatures.filter(f => f.trim()).join('|'),
        platinum_features: platFeatures.filter(f => f.trim()).join('|'),
      });
      setProMonthlyPrice(result.pro_monthly_price);
      setProYearlyPrice(result.pro_yearly_price);
      setAdvMonthlyPrice(result.advanced_monthly_price);
      setAdvYearlyPrice(result.advanced_yearly_price);
      setPlatMonthlyPrice(result.platinum_monthly_price);
      setPlatYearlyPrice(result.platinum_yearly_price);
      setProFeatures(result.pro_features ? result.pro_features.split('|') : []);
      setAdvFeatures(result.advanced_features ? result.advanced_features.split('|') : []);
      setPlatFeatures(result.platinum_features ? result.platinum_features.split('|') : []);
      setSavedTiers(true);
      setTimeout(() => setSavedTiers(false), 3000);
    } catch (e: any) {
      setErrMsg(e.message);
    } finally {
      setSavingTiers(false);
    }
  };

  const addFeature = (list: string[], setList: (v: string[]) => void) => {
    setList([...list, '']);
  };

  const updateFeature = (list: string[], setList: (v: string[]) => void, idx: number, val: string) => {
    const updated = [...list];
    updated[idx] = val;
    setList(updated);
  };

  const removeFeature = (list: string[], setList: (v: string[]) => void, idx: number) => {
    setList(list.filter((_, i) => i !== idx));
  };

  if (loading) return <LoadingSpinner />;

  const renderFeatureEditor = (label: string, color: string, features: string[], setFeatures: (v: string[]) => void) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
        <button
          onClick={() => addFeature(features, setFeatures)}
          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add Feature
        </button>
      </div>
      <div className="space-y-1.5">
        {features.map((f, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={f}
              onChange={(e) => updateFeature(features, setFeatures, i, e.target.value)}
              className="flex-1 border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Feature description..."
            />
            <button
              onClick={() => removeFeature(features, setFeatures, i)}
              className="text-gray-400 hover:text-red-500 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {features.length === 0 && (
          <p className="text-xs text-gray-400 italic">No features configured</p>
        )}
      </div>
    </div>
  );

  const eventTypeLabel = (type: string) => {
    const map: Record<string, { label: string; color: string }> = {
      'checkout.session.completed': { label: 'Checkout', color: 'bg-green-100 text-green-700' },
      'customer.subscription.updated': { label: 'Sub Updated', color: 'bg-blue-100 text-blue-700' },
      'customer.subscription.deleted': { label: 'Sub Deleted', color: 'bg-red-100 text-red-700' },
      'invoice.payment_failed': { label: 'Payment Failed', color: 'bg-amber-100 text-amber-700' },
    };
    return map[type] || { label: type, color: 'bg-gray-100 text-gray-700' };
  };

  return (
    <div className="space-y-4">
      {errMsg && <ErrorMessage message={errMsg} />}

      {/* Stripe Configuration */}
      <Accordion title="Stripe Configuration" subtitle="API keys, webhook secret, and price IDs">
        <div className="p-6 space-y-5">
          {/* Secret Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Secret Key</label>
            {currentSecretKey && <p className="text-xs text-gray-500 mb-1">Current: {currentSecretKey}</p>}
            <div className="relative">
              <input
                type={showSecretKey ? 'text' : 'password'}
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder={currentSecretKey ? 'Enter new key to update' : 'sk_live_... or sk_test_...'}
                className="w-full border rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button type="button" onClick={() => setShowSecretKey(!showSecretKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Webhook Secret */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Signing Secret</label>
            {currentWebhookSecret && <p className="text-xs text-gray-500 mb-1">Current: {currentWebhookSecret}</p>}
            <div className="relative">
              <input
                type={showWebhookSecret ? 'text' : 'password'}
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={currentWebhookSecret ? 'Enter new secret to update' : 'whsec_...'}
                className="w-full border rounded-lg px-3 py-2 pr-10 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button type="button" onClick={() => setShowWebhookSecret(!showWebhookSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Price IDs — Pro */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pro Plan Price IDs</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Monthly</label>
                <input type="text" value={proMonthlyPriceId} onChange={(e) => setProMonthlyPriceId(e.target.value)} placeholder="price_..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Yearly</label>
                <input type="text" value={proYearlyPriceId} onChange={(e) => setProYearlyPriceId(e.target.value)} placeholder="price_..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>
          </div>

          {/* Price IDs — Advanced */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Advanced Plan Price IDs</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Monthly</label>
                <input type="text" value={advMonthlyPriceId} onChange={(e) => setAdvMonthlyPriceId(e.target.value)} placeholder="price_..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Yearly</label>
                <input type="text" value={advYearlyPriceId} onChange={(e) => setAdvYearlyPriceId(e.target.value)} placeholder="price_..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>
          </div>

          {/* Price IDs — Platinum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platinum Plan Price IDs</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Monthly</label>
                <input type="text" value={platMonthlyPriceId} onChange={(e) => setPlatMonthlyPriceId(e.target.value)} placeholder="price_..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Yearly</label>
                <input type="text" value={platYearlyPriceId} onChange={(e) => setPlatYearlyPriceId(e.target.value)} placeholder="price_..." className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>
          </div>

          {/* Webhook URL Info */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Webhook Endpoint URL</p>
            <code className="text-xs bg-gray-900 text-green-400 px-3 py-1.5 rounded block">
              https://api.mycoursesearch.com/api/stripe/webhook
            </code>
            <p className="text-xs text-gray-500 mt-2">
              Subscribe to: <span className="font-mono">checkout.session.completed</span>, <span className="font-mono">customer.subscription.updated</span>, <span className="font-mono">customer.subscription.deleted</span>, <span className="font-mono">invoice.payment_failed</span>
            </p>
          </div>

          {/* Save Stripe */}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleSaveStripe} disabled={savingStripe} className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
              {savingStripe ? <><RefreshCw className="w-4 h-4 animate-spin" />Saving...</> : 'Save Stripe Config'}
            </button>
            {savedStripe && <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" />Saved</span>}
          </div>
        </div>
      </Accordion>

      {/* Plan Tiers */}
      <Accordion title="Plan Tiers" subtitle="Pricing and features for each plan">
        <div className="p-6 space-y-6">
          {/* Pricing Grid */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Pricing (USD)</label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">Pro</span>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-gray-500">Monthly ($)</label>
                    <input type="number" value={proMonthlyPrice} onChange={(e) => setProMonthlyPrice(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Yearly ($)</label>
                    <input type="number" value={proYearlyPrice} onChange={(e) => setProYearlyPrice(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                    {Number(proYearlyPrice) > 0 && Number(proMonthlyPrice) > 0 && (
                      <p className="text-xs text-green-600 mt-0.5">
                        ${(Number(proYearlyPrice) / 12).toFixed(0)}/mo — save {Math.round((1 - Number(proYearlyPrice) / (Number(proMonthlyPrice) * 12)) * 100)}%
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mb-2">Advanced</span>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-gray-500">Monthly ($)</label>
                    <input type="number" value={advMonthlyPrice} onChange={(e) => setAdvMonthlyPrice(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Yearly ($)</label>
                    <input type="number" value={advYearlyPrice} onChange={(e) => setAdvYearlyPrice(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                    {Number(advYearlyPrice) > 0 && Number(advMonthlyPrice) > 0 && (
                      <p className="text-xs text-green-600 mt-0.5">
                        ${(Number(advYearlyPrice) / 12).toFixed(0)}/mo — save {Math.round((1 - Number(advYearlyPrice) / (Number(advMonthlyPrice) * 12)) * 100)}%
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 mb-2">Platinum</span>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-gray-500">Monthly ($)</label>
                    <input type="number" value={platMonthlyPrice} onChange={(e) => setPlatMonthlyPrice(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Yearly ($)</label>
                    <input type="number" value={platYearlyPrice} onChange={(e) => setPlatYearlyPrice(e.target.value)} className="w-full border rounded-lg px-3 py-1.5 text-sm" />
                    {Number(platYearlyPrice) > 0 && Number(platMonthlyPrice) > 0 && (
                      <p className="text-xs text-green-600 mt-0.5">
                        ${(Number(platYearlyPrice) / 12).toFixed(0)}/mo — save {Math.round((1 - Number(platYearlyPrice) / (Number(platMonthlyPrice) * 12)) * 100)}%
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Lists */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Plan Features</label>
            <div className="space-y-5">
              {renderFeatureEditor('Pro', 'bg-blue-100 text-blue-800', proFeatures, setProFeatures)}
              {renderFeatureEditor('Advanced', 'bg-purple-100 text-purple-800', advFeatures, setAdvFeatures)}
              {renderFeatureEditor('Platinum', 'bg-amber-100 text-amber-800', platFeatures, setPlatFeatures)}
            </div>
          </div>

          {/* Save Tiers */}
          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleSaveTiers} disabled={savingTiers} className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
              {savingTiers ? <><RefreshCw className="w-4 h-4 animate-spin" />Saving...</> : 'Save Plan Tiers'}
            </button>
            {savedTiers && <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" />Saved</span>}
          </div>
        </div>
      </Accordion>

      {/* Tier Limits Enforcement */}
      <TierLimitsEditor />

      {/* Stripe Webhook Logs */}
      <Accordion title="Stripe Webhook Logs" subtitle="Recent webhook events received from Stripe">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{webhookLogs.length > 0 ? `${webhookLogs.length} events` : 'Click refresh to load logs'}</p>
            <button
              onClick={loadWebhookLogs}
              disabled={logsLoading}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {webhookLogs.length === 0 && !logsLoading && (
            <div className="text-center py-8 text-gray-400 text-sm">
              No webhook events recorded yet.
            </div>
          )}

          {logsLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
            </div>
          )}

          {webhookLogs.length > 0 && (
            <div className="divide-y divide-gray-100 border rounded-lg overflow-hidden">
              {webhookLogs.map(log => {
                const { label, color } = eventTypeLabel(log.event_type);
                const isExpanded = expandedLog === log.id;
                let parsedPayload: Record<string, unknown> | null = null;
                try {
                  parsedPayload = log.payload ? JSON.parse(log.payload) : null;
                } catch { /* ignore */ }

                return (
                  <div key={log.id} className="bg-white">
                    <button
                      onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {log.status === 'success' ? 'OK' : 'ERR'}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
                        {label}
                      </span>
                      <span className="text-xs text-gray-500 flex-1 truncate font-mono">
                        {log.stripe_event_id || '—'}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-2">
                        {log.customer_id && (
                          <div className="flex gap-2 text-xs">
                            <span className="text-gray-500 font-medium">Customer ID:</span>
                            <span className="font-mono text-gray-700">{log.customer_id}</span>
                          </div>
                        )}
                        {log.error_message && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs text-red-700">{log.error_message}</p>
                          </div>
                        )}
                        {parsedPayload && (
                          <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-48">
                            {JSON.stringify(parsedPayload, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Accordion>
    </div>
  );
}

// ============ Tier Limits Editor ============

const LIMIT_RESOURCES = [
  { key: 'courses', label: 'Courses' },
  { key: 'lessons', label: 'Lessons' },
  { key: 'widgets', label: 'Widgets' },
  { key: 'api_keys', label: 'API Keys' },
  { key: 'domains', label: 'Domains' },
  { key: 'searches_monthly', label: 'Searches/mo' },
  { key: 'team_members', label: 'Team Members' },
] as const;

const LIMIT_TIERS = [
  { key: 'pro', label: 'Pro', badge: 'bg-blue-100 text-blue-800' },
  { key: 'advanced', label: 'Advanced', badge: 'bg-purple-100 text-purple-800' },
  { key: 'platinum', label: 'Platinum', badge: 'bg-amber-100 text-amber-800' },
] as const;

function TierLimitsEditor() {
  const [limitsConfig, setLimitsConfig] = useState<LimitsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    api.getAdminLimitsConfig()
      .then(setLimitsConfig)
      .catch((e) => setErrMsg(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!limitsConfig) return;
    setSaving(true);
    setErrMsg('');
    setSaved(false);
    try {
      const result = await api.saveAdminLimitsConfig(limitsConfig);
      setLimitsConfig(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setErrMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: string) => {
    if (!limitsConfig) return;
    setLimitsConfig({ ...limitsConfig, [field]: value });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold text-gray-900">Tier Limits (Enforcement)</h3>
        <p className="text-sm text-gray-500 mt-1">
          Set the actual enforced limits for each plan. Use <code className="text-xs bg-gray-100 px-1 rounded">-1</code> for unlimited.
        </p>
      </div>

      {errMsg && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{errMsg}</p>
        </div>
      )}

      <div className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-gray-700">Resource</th>
                {LIMIT_TIERS.map(tier => (
                  <th key={tier.key} className="py-2 px-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tier.badge}`}>
                      {tier.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LIMIT_RESOURCES.map(resource => (
                <tr key={resource.key} className="border-b last:border-b-0">
                  <td className="py-3 pr-4 font-medium text-gray-700">{resource.label}</td>
                  {LIMIT_TIERS.map(tier => {
                    const field = `${tier.key}_${resource.key}` as keyof LimitsConfig;
                    const val = limitsConfig?.[field] || '0';
                    const isUnlimited = val === '-1';
                    return (
                      <td key={tier.key} className="py-3 px-3">
                        <div className="flex items-center gap-1.5 justify-center">
                          <input
                            type="number"
                            value={val}
                            onChange={(e) => updateField(field, e.target.value)}
                            className={`w-24 border rounded-lg px-2 py-1.5 text-sm text-center ${isUnlimited ? 'bg-green-50 border-green-300' : ''}`}
                          />
                          {isUnlimited && <span className="text-xs text-green-600 font-medium">∞</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 pt-4 mt-4 border-t">
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <><RefreshCw className="w-4 h-4 animate-spin" />Saving...</> : 'Save Limits'}
          </button>
          {saved && <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" />Saved</span>}
        </div>
      </div>
    </div>
  );
}

// ============ Shared Components ============

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border p-4">
      <div className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      {message}
    </div>
  );
}
